<template>
  <form
      class="form"
      enctype="multipart/form-data"
      novalidate
      @submit.prevent="handleSubmit"
  >

    <FormErrorsList v-if="errorsMessages.length"
                    :errorsList="errorsMessages"
                    :title="t('global.formErrorNotification')"/>

    <ProductBaseForm
        :product="activity"
        :title="t('products.form.fieldset-informations-activity')"
        @handle-validation="handleValidation"
        @register-form-input-ref="addFormInputRef"
    >
      <template v-slot:image>
        <FormFiles
            :ref="addFormInputRef"
            v-model="activity.pageImage"
            :default-selected-files="activity.savedPageImage != null ? [activity.savedPageImage] : []"
            :label="t('products.form.image-page')"
            :possiblesTypes="['image/png', 'image/jpg', 'image/jpeg', 'image/webp']"
            :tooltip="t(`products.form.image-page-tooltip`)"
            can-delete
            name="pageImage"
            @change="onImageChange($event, 'page')"
            @validated="handleValidation"
            @on-default-selected-files-change="onSavedImageChange($event, 'page')"
        />
      </template>
      <template v-slot:after-images>
        <FormRow>
          <FormInput
              :ref="addFormInputRef"
              v-model="activity.imageOverlayTextFr"
              :label="t('products.form.image-text-fr')"
              :rules="[]"
              name="image-text-fr"
              type="text"
              @validated="handleValidation"/>
          <FormInput
              :ref="addFormInputRef"
              v-model="activity.imageOverlayTextEn"
              :label="t('products.form.image-text-en')"
              :rules="[]"
              name="image-text-en"
              type="text"
              @validated="handleValidation"/>
        </FormRow>
      </template>
    </ProductBaseForm>

    <Fieldset :title="t('activities.form.fieldset-schedule')">
      <FormRow>
        <FormInput
            :ref="addFormInputRef"
            v-model="activity.startsAt"
            :label="t('activities.form.startsAt')"
            :rules="[]"
            name="starts-at"
            type="time"
            @validated="handleValidation"
        />
        <FormInput
            :ref="addFormInputRef"
            v-model="activity.endsAt"
            :label="t('activities.form.endsAt')"
            :rules="[]"
            name="ends-at"
            type="time"
            @validated="handleValidation"
        />
      </FormRow>
      <FormRow>
        <FormInput
            :ref="addFormInputRef"
            v-model="activity.trainer"
            :label="t('activities.form.trainer')"
            :rules="[]"
            name="trainer"
            type="text"
            @validated="handleValidation"
        />
      </FormRow>
    </Fieldset>

    <ActivityOccurrenceForm ref="activityOccurenceForm"
                            v-model:model-value="activity.activityOccurrences"
                            :activity-id="props.id"
                            @validated="handleValidation"
                            @updated-activity-occurrences="onActivityOccurrencesChange"/>

    <Fieldset :title="t('activities.form.fieldset-location')">
      <FormRadios
          :ref="addFormInputRef"
          v-model="activity.location"
          :isRequired="true"
          :label="t('activities.form.location')"
          :options="locationOptions"
          name="location"
          @validated="handleValidation"
      />
      <FormRow with-three-columns>
        <FormInput
            v-if="!isOnline"
            :ref="addFormInputRef"
            v-model="activity.locationAddress"
            :label="t('activities.form.address')"
            :rules="[]"
            name="location-address"
            type="text"
            @validated="handleValidation"
        />
        <FormInput
            v-if="isOnline"
            :ref="addFormInputRef"
            v-model="activity.onlineUrl"
            :label="t('activities.form.url')"
            :rules="[]"
            name="online-url"
            type="text"
            @validated="handleValidation"
        />
        <FormInput
            v-if="isOnline"
            :ref="addFormInputRef"
            v-model="activity.onlinePhoneNumber"
            :label="t('global.phoneNumber')"
            :rules="[required, mustMatchPhoneNumberFormat]"
            name="online-phone-number"
            type="tel"
            @validated="handleValidation"
        />
        <FormInput
            v-if="isOnline"
            :ref="addFormInputRef"
            v-model="activity.onlineWebinarNumber"
            :label="t('activities.form.webinarNumber')"
            :rules="[]"
            name="online-webinar-number"
            type="text"
            @validated="handleValidation"
        />
      </FormRow>
    </Fieldset>

    <Fieldset :title="t('activities.form.fieldset-participants')">
      <FormRow>
        <FormInput
            :ref="addFormInputRef"
            v-model="activity.maxParticipants"
            :label="t('activities.form.maxParticipants')"
            :rules="[required, min(1)]"
            name="maxParticipants"
            type="number"
            @validated="handleValidation"
        />
      </FormRow>
      <FormRow>
        <FormInput
            :ref="addFormInputRef"
            v-model="activity.surveyUrl"
            :label="t('activityOccurrences.form.surveyUrl')"
            :rules="[]"
            name="surveyUrl"
            type="text"
            @validated="handleValidation"
        />
      </FormRow>
      <FormRow>
        <FormCheckbox
            v-model="activity.isPrivate"
            :label="t('activities.form.private')"
            name="isPrivate"
            @validated="handleValidation"
        />
        <FormCheckbox
            v-model="activity.isPublished"
            :label="t('activities.form.publish')"
            name="isPublished"
            @validated="handleValidation"
        />
      </FormRow>
    </Fieldset>

    <Fieldset :title="t('activities.form.fieldset-documents')">
      <FormRow>
        <FormInput
            :ref="addFormInputRef"
            v-model="activity.activityRecordingUrl"
            :label="t('activityOccurrences.form.activityRecordingUrl')"
            :rules="[]"
            name="activityRecordingUrl"
            type="text"
            @validated="handleValidation"
        />
      </FormRow>
      <FormRow>
        <FormFiles
            v-model="activity.participantGuideUrl"
            :default-selected-files="activity.participantGuideUrl != undefined ? [activity.participantGuideUrl] : []"
            :label="t('activityOccurrences.form.participantGuideUrl')"
            name="participantGuideUrl"
            @change="onImageChange($event, 'participantGuideUrl')"
            @validated="handleValidation"
        />

        <FormFiles
            v-model="activity.activityPresentationUrl"
            :default-selected-files="activity.activityPresentationUrl != undefined ? [activity.activityPresentationUrl] : []"
            :label="t('activityOccurrences.form.activityPresentationUrl')"
            name="activityPresentationUrl"
            @change="onImageChange($event, 'activityPresentationUrl')"
            @validated="handleValidation"
        />
      </FormRow>
      <FormTooltip>
        <p v-html="t(`global.uploadZippedFolder-tooltip`)"></p>
      </FormTooltip>
    </Fieldset>

    <button
        v-if="!isEditMode"
        class="form__submit btn btn--fullscreen"
        type="submit"
    >
      {{ t("global.add") }}
    </button>
    <div v-else class="form__submit">
      <button
          class="btn btn--fullscreen"
          type="submit"
      >
        {{ t("global.update") }}
      </button>
      <button
          class="btn btn--red btn--fullscreen"
          type="button"
          @click="deleteActivity"
      >
        {{ t("global.delete") }}
      </button>
    </div>
  </form>
</template>

<script lang="ts" setup>
import {computed, ref} from "vue";
import {Status} from "@/validation";
import {min, mustMatchPhoneNumberFormat, required} from "@/validation/rules";
import FormInput from "@/components/forms/FormInput.vue";
import Fieldset from "@/components/forms/Fieldset.vue";
import FormRow from "@/components/forms/FormRow.vue";
import FormRadios from "@/components/forms/FormRadios.vue";
import ProductBaseForm from "@/components/products/ProductBaseForm.vue";
import FormErrorsList from "@/components/forms/FormErrorsList.vue";
import FormTooltip from "@/components/forms/FormTooltip.vue";

import {useI18n} from "vue3-i18n";
import {Guid} from "@/types";
import {useRouter} from "vue-router";
import {useActivityService} from "@/inversify.config";
import {notifyError, notifySuccess} from "@/notify";
import {SucceededOrNotResponse} from "@/types/responses/succeededOrNotResponse";
import {Activity, ActivityLocation} from "@/types/entities/activity";
import FormCheckbox from "@/components/forms/FormCheckbox.vue";
import FormFiles from "@/components/forms/FormFiles.vue";
import ActivityOccurrenceForm from "@/components/activities/ActivityOccurrenceForm.vue";

// eslint-disable-next-line no-undef
const props = defineProps<{
  id?: string
}>()

const {t} = useI18n();

const router = useRouter();
const activityService = useActivityService();

const isEditMode = props.id && Guid.isValid(props.id);
const activity = ref<Activity>(isEditMode ? await activityService.getActivity(props.id) : new Activity())

const formInputs = ref<any[]>([])
const activityOccurenceForm = ref()
const inputValidationStatuses: any = {}

const locationOptions = [
  {name: ActivityLocation.InPerson, label: t("activities.form.location-presential")},
  {name: ActivityLocation.Online, label: t("activities.form.location-online")},
];

const isOnline = computed(() => {
  if (!activity.value.location)
    return false;
  return activity.value.location == ActivityLocation.Online;
});

async function onImageChange(event: any, type: string) {
  const file = event?.target?.files[0];
  if (type === "page")
    activity.value.pageImage = file as File;
  if (type === "participantGuideUrl")
    activity.value.participantGuideUrl = file as File;
  if (type === "activityPresentationUrl")
    activity.value.activityPresentationUrl = file as File;
}

async function onSavedImageChange(url: any, type: string) {
  if (type === "page") activity.value.savedPageImage = url
}

async function onActivityOccurrencesChange(activityOccurrences: any) {
  activity.value.activityOccurrences = activityOccurrences;
}


function addFormInputRef(ref: typeof FormInput) {
  if (!formInputs.value.includes(ref) && ref)
    formInputs.value.push(ref)
}

async function handleValidation(name: string, validationStatus: Status) {
  inputValidationStatuses[name] = validationStatus.valid
}

async function handleSubmit() {
  formInputs.value.forEach((x: typeof FormInput) => x.validateInput())
  await activityOccurenceForm.value.validateForm()

  if (activity.value.activityOccurrences.length == 0) {
    notifyError(t('activityOccurrences.form.mustEnterAtLeastOneOccurrence'))
    return;
  }

  let validKeyNames = Object.keys(inputValidationStatuses).filter(x => x.length);
  if (Object.values(validKeyNames.map(key => inputValidationStatuses[key])).some(x => x === false)) {
    notifyError(t('global.formErrorNotification'))
    return
  } else {
    await save();
  }
}

async function save() {
  let succeededOrNotResponse: SucceededOrNotResponse
  activity.value.activityOccurrences.forEach(occurrence => {
    occurrence.startDate = occurrence.startDate ? occurrence.startDate : "";
    occurrence.endDate = occurrence.endDate ? occurrence.endDate : "";
  })

  if (!activity.value.activityOccurrences.every(x => x.startDate?.length)) {
    notifyError(t('activities.form.saveError'))
    return;
  }

  let request: any = {
    ...activity.value,
    activityOccurrences: JSON.stringify(activity.value.activityOccurrences)
  }
  if (isEditMode) {
    succeededOrNotResponse = await activityService.updateActivity(request);
  } else {
    succeededOrNotResponse = await activityService.createActivity(request);
  }

  if (succeededOrNotResponse.succeeded) {
    notifySuccess(t('activities.form.saveSuccess'))
    setTimeout(() => {
      router.back();
    }, 1500);
  } else {
    notifyError(t('activities.form.saveError'))
    displayErrors(succeededOrNotResponse)
  }
}

async function deleteActivity() {
  if (!Guid.isValid(activity.value.id ?? "") || activity.value.id == null)
    return;

  let confirmDelete = confirm(t("activities.form.deleteConfirmation"));

  if (!confirmDelete) return;

  let result: SucceededOrNotResponse = await activityService.deleteActivity(activity.value.id);
  if (result?.succeeded) {
    notifySuccess(t('activities.form.deleteSuccess'))
    await router.push({name: "admin.children.activities.index"});
  } else {
    displayErrors(result as SucceededOrNotResponse)
    notifyError(t('activities.form.deleteError'))
  }
}

const errorsMessages = ref<string[]>([])

function displayErrors(response: SucceededOrNotResponse) {
  response = new SucceededOrNotResponse(response.succeeded, response.errors)
  if (response?.succeeded != undefined && !response?.succeeded) {
    errorsMessages.value = response.getErrorMessages('validation.activity', 'validation.activity.errorOccured')
  } else errorsMessages.value = []
}
</script>